import { memo, useCallback, useEffect, useState } from 'react';
import Lottie from 'react-lottie';
import { Button, Link, Modal, Timeout, Typography } from 'components';
import ConnectionFailedIcon from 'assets/icons/connection-failed.svg?react';
import PhoneIcon from 'assets/icons/phone.svg?react';
import PhoneWarningIcon from 'assets/icons/phone-warning.svg?react';
import { useDocumentUpload } from 'hooks/webapi';
import ConnectionLottie from 'assets/lottie/connection.json';
import { clearTimer, isTimerAlreadyStarted } from 'utils/timeoutUtility.ts';
import styles from './MagicLinkModal.module.scss';
import { useTranslation } from 'react-i18next';

const connectionLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: ConnectionLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  dataTest?: string;
};

type Status = 'CONNECTING' | 'EXPIRED' | 'PROCESSING' | 'FAILED';

// eslint-disable-next-line react-refresh/only-export-components
export const COUNTDOWN_MAGIC_LINK = 60 * 2; // s
const POLLING_INTERVAL = 1000 * 2; // ms

const MagicLinkModalMemo = memo(function MagicLinkModal({
  onSuccess,
  onCancel,
  dataTest = 'magicLinkModal',
}: Props) {
  const { t } = useTranslation();
  const { sendSms, getUploadStatus } = useDocumentUpload();
  const [status, setStatus] = useState<Status>('CONNECTING');

  const asyncSendSms = useCallback(async () => {
    try {
      await sendSms();
    } catch {
      setStatus('FAILED');
    }
  }, [sendSms]);

  useEffect(() => {
    if (!isTimerAlreadyStarted(COUNTDOWN_MAGIC_LINK)) asyncSendSms();
  }, [asyncSendSms]);

  const asyncUploadStatus = useCallback(async () => {
    try {
      const uploadStatus = await getUploadStatus();

      if (!uploadStatus) {
        return;
      }
      // clear timer because BE already process the petition
      clearTimer();

      if (uploadStatus.status === 'USER_STARTED') {
        setStatus('PROCESSING');
      } else if (uploadStatus.status === 'DONE') {
        if (onSuccess) {
          onSuccess();
        }
      } else if (uploadStatus.status === 'EXPIRED') {
        setStatus('EXPIRED');
      } else if (uploadStatus?.status === 'ERROR') {
        setStatus('FAILED');
      }
    } catch {
      onCancel();
    }
  }, [getUploadStatus, onSuccess, onCancel]);

  useEffect(() => {
    if (status !== 'CONNECTING' && status !== 'PROCESSING') return;
    const polling = setInterval(async () => {
      await asyncUploadStatus();
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(polling);
    };
  }, [status, asyncUploadStatus]);

  const handleRetry = useCallback(() => {
    setStatus('CONNECTING');
    asyncSendSms();
  }, [asyncSendSms]);

  const handleTimeout = useCallback(() => {
    setStatus('EXPIRED');
  }, []);

  return (
    <Modal
      dataTest={dataTest}
      className={styles.card}
      contentClassName={styles.cardContent}
    >
      {status === 'CONNECTING' && (
        <>
          <PhoneIcon
            className={styles.icon}
            data-test={`${dataTest}-phone-icon`}
          />
          <Typography
            className={styles.title}
            size="l"
            kind="h3"
            dataTest={`${dataTest}-title`}
          >
            {t('identification_conecting:title')}
          </Typography>
          <Typography className={styles.hint} dataTest={`${dataTest}-hint`}>
            {t('identification_conecting:hint')}
          </Typography>
          <Typography
            className={styles['time-info']}
            dataTest={`${dataTest}-time`}
          >
            {t('identification_conecting:time')}
          </Typography>
          <Timeout
            seconds={COUNTDOWN_MAGIC_LINK}
            onTimeout={handleTimeout}
            dataTest={`${dataTest}-timeout`}
          />
        </>
      )}

      {status === 'EXPIRED' && (
        <>
          <PhoneWarningIcon
            className={styles.icon}
            data-test={`${dataTest}-warning-icon`}
          />
          <Typography
            className={styles.title}
            size="l"
            kind="h3"
            dataTest={`${dataTest}-expired-title`}
          >
            {t('identification_expired:title')}
          </Typography>
          <Button
            className={styles.button}
            onClick={handleRetry}
            dataTest={`${dataTest}-expired-confirm`}
          >
            {t('identification_expired:confirm')}
          </Button>
          <Link onClick={onCancel} dataTest={`${dataTest}-link-close`}>
            {t('identification_expired:close')}
          </Link>
        </>
      )}

      {status === 'PROCESSING' && (
        <>
          <div
            className={styles.connecting}
            data-test={`${dataTest}-lottie-wrapper`}
          >
            <Lottie options={connectionLottieOptions} />
          </div>
          <Typography
            className={styles.title}
            size="l"
            kind="h3"
            dataTest={`${dataTest}-processing-title`}
          >
            {t('identification_processing:title')}
          </Typography>
          <Typography
            className={styles.hint}
            dataTest={`${dataTest}-processing-hint`}
          >
            {t('identification_processing:hint')}
          </Typography>
        </>
      )}

      {status === 'FAILED' && (
        <>
          <ConnectionFailedIcon
            className={styles.icon}
            data-test={`${dataTest}-failed-icon`}
          />
          <Typography
            className={styles.title}
            size="l"
            kind="h3"
            dataTest={`${dataTest}-failed-title`}
          >
            {t('identification_failed:title')}
          </Typography>
          <Button onClick={handleRetry} dataTest={`${dataTest}-failed-retry`}>
            {t('identification_failed:retry')}
          </Button>
        </>
      )}
    </Modal>
  );
});

export default MagicLinkModalMemo;
